import React from 'react';
import {
  CheckCircle2,
  XCircle,
  Clock,
  BookOpen,
  Archive,
  ShieldCheck,
} from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';

interface BookDetailAdminStatusControlProps {
  book: Book;
  user: any;
  isAdmin: boolean;
  isPublisherOwner: boolean;
  statusNote: string;
  isUpdatingStatus: boolean;
  onStatusNoteChange: (note: string) => void;
  onStatusChange: (status: string, note?: string) => void;
  onOpenPublisherVerifyModal?: () => void;
}

export const BookDetailAdminStatusControl: React.FC<BookDetailAdminStatusControlProps> = ({
  book,
  user,
  isAdmin,
  isPublisherOwner,
  statusNote,
  isUpdatingStatus,
  onStatusNoteChange,
  onStatusChange,
  onOpenPublisherVerifyModal,
}) => {
  return (
    <div className="space-y-4">
      {/* Rejection notice alert banner for Author & Publisher */}
      {book.status === 'ditolak' && (
        <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-900 space-y-1.5">
          <div className="flex items-center gap-2 font-bold text-rose-800">
            <XCircle className="w-4 h-4 text-rose-600 shrink-0" />
            <span>Pengajuan UIBN Ditolak / Perlu Perbaikan Metadata</span>
          </div>
          <p className="text-slate-600 leading-relaxed">
            Pengajuan nomor UIBN untuk buku ini saat ini <strong>DITOLAK</strong> oleh Admin. Penulis atau Penerbit dapat memeriksa kelengkapan metadata naskah lalu menekan tombol <strong>"Ajukan Verifikasi Ulang"</strong>.
          </p>
        </div>
      )}

      {/* Admin Status Management */}
      <div className="pt-4 border-t border-slate-200 space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-800 uppercase tracking-wide">
            {isPublisherOwner && book.status === 'menunggu_penerbit'
              ? 'Kurasi Naskah Masuk dari Penulis'
              : 'Kontrol Status Buku & Verifikasi UIBN'}
          </label>
          <span className="text-[11px] text-slate-500">
            Role aktif: <strong className="uppercase">{user?.role}</strong>{' '}
            {isAdmin ? '(Administrator)' : isPublisherOwner ? '(Penerbit)' : ''}
          </span>
        </div>

        {/* Status Note Input */}
        <div>
          <input
            type="text"
            value={statusNote}
            onChange={(e) => onStatusNoteChange(e.target.value)}
            placeholder={
              isPublisherOwner && book.status === 'menunggu_penerbit'
                ? 'Catatan revisi / alasan penolakan naskah (opsional)...'
                : 'Catatan persetujuan / alasan penolakan (opsional)...'
            }
            className="w-full text-xs px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:bg-white text-slate-800 placeholder:text-slate-400"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Publisher Kurasi Actions */}
          {isPublisherOwner && book.status === 'menunggu_penerbit' && (
            <>
              <button
                type="button"
                disabled={isUpdatingStatus}
                onClick={() => {
                  if (onOpenPublisherVerifyModal) {
                    onOpenPublisherVerifyModal();
                  } else {
                    onStatusChange('diajukan');
                  }
                }}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-50 cursor-pointer shadow-2xs"
                title="Verifikasi checklist kelayakan naskah sebelum dikirim ke Admin UIBN"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Verifikasi</span>
              </button>
              <button
                type="button"
                disabled={isUpdatingStatus}
                onClick={() => onStatusChange('draft')}
                className="px-3 py-1.5 bg-slate-600 hover:bg-slate-700 active:bg-slate-800 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-50 cursor-pointer shadow-2xs"
              >
                <XCircle className="w-3.5 h-3.5" />
                <span>Tolak / Minta Revisi Naskah</span>
              </button>
            </>
          )}

          {/* Approve button: Admin role only */}
          {isAdmin && book.status !== 'disetujui' && (
            <button
              type="button"
              disabled={isUpdatingStatus}
              onClick={() => onStatusChange('disetujui')}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-50 cursor-pointer shadow-2xs"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Setujui & Terbitkan UIBN</span>
            </button>
          )}

          {/* Reject button: Admin role only */}
          {isAdmin && book.status !== 'ditolak' && (
            <button
              type="button"
              disabled={isUpdatingStatus}
              onClick={() => onStatusChange('ditolak')}
              className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition disabled:opacity-50 cursor-pointer shadow-2xs"
            >
              <XCircle className="w-3.5 h-3.5" />
              <span>Tolak Pengajuan</span>
            </button>
          )}

          {/* Submit for verification: Author can submit draft, or publisher/admin/author can re-submit */}
          {book.status !== 'diajukan' && (
            <button
              type="button"
              disabled={isUpdatingStatus}
              onClick={() => onStatusChange('diajukan')}
              className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition disabled:opacity-50 cursor-pointer shadow-2xs"
            >
              <Clock className="w-3.5 h-3.5" />
              <span>{book.status === 'ditolak' ? 'Ajukan Verifikasi Ulang' : 'Ajukan Verifikasi'}</span>
            </button>
          )}

          {/* Return to draft: All authorized parties */}
          {book.status !== 'draft' && (
            <button
              type="button"
              disabled={isUpdatingStatus}
              onClick={() => onStatusChange('draft')}
              className="px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition disabled:opacity-50 cursor-pointer shadow-2xs"
            >
              <BookOpen className="w-3.5 h-3.5" />
              <span>Kembalikan ke Draft</span>
            </button>
          )}

          {/* Archive: Admin & Publisher only */}
          {(isAdmin || isPublisherOwner) && book.status !== 'diarsipkan' && (
            <button
              type="button"
              disabled={isUpdatingStatus}
              onClick={() => onStatusChange('diarsipkan')}
              className="px-3 py-1.5 bg-stone-500 hover:bg-stone-600 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition disabled:opacity-50 cursor-pointer shadow-2xs"
            >
              <Archive className="w-3.5 h-3.5" />
              <span>Arsipkan</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
